import React, { FC, useEffect, useState } from "react";
import CreateBonusField from "./shared/CreateBonusField";
import { TextField, Box, Switch } from "@material-ui/core";
import DateTimePicker from "react-datetime-picker";
import { AppButton } from "../common/ctaButton/button";
import { useDataProvider, useNotify } from "react-admin";

interface State {
  value: string;
  isActive: boolean;
  badgeURL: string;
  badgeMobileURL: string;
  title: string;
  description: string;
  startAt: string;
  endAt: string;
}

interface Errors {
  value: { status: boolean; message: string };
  badgeURL: { status: boolean; message: string };
  badgeMobileURL: { status: boolean; message: string };
  title: { status: boolean; message: string };
  description: { status: boolean; message: string };
  startAt: { status: boolean; message: string };
  endAt: { status: boolean; message: string };
}

const errorsInitialState: Errors = {
  value: { status: false, message: "" },
  badgeURL: { status: false, message: "" },
  badgeMobileURL: { status: false, message: "" },
  title: { status: false, message: "" },
  description: { status: false, message: "" },
  startAt: { status: false, message: "" },
  endAt: { status: false, message: "" },
};

const FlashDraw: FC = () => {
  const [state, setState] = useState<State>({
    value: "",
    isActive: false,
    badgeURL: "",
    badgeMobileURL: "",
    title: "",
    description: "",
    startAt: "",
    endAt: "",
  });

  const [errors, setErrors] = useState<Errors>(errorsInitialState);

  const [touchState, setTouch] = useState({
    startAt: false,
    endAt: false,
    value: false,
    badgeURL: false,
    badgeMobileURL: false,
    title: false,
    description: false,
    isActive: false,
  });

  const notify = useNotify();
  const dataProvider = useDataProvider();

  const getFlashDraw = async (): Promise<void> => {
    const result = (await dataProvider.getFlashDraw("/flash-draw")) as any;
    const data = result.data;

    if (data && data._id) {
      setState({
        value: data.value || "",
        isActive: !!data.isActive,
        badgeURL: data.badgeURL || "",
        badgeMobileURL: data.badgeMobileURL || "",
        title: data.title || "",
        description: data.description || "",
        startAt: data.startAt || "",
        endAt: data.endAt || "",
      });
    }
  };

  useEffect(() => {
    getFlashDraw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFormCancel = () => {
    setErrors({
      ...errorsInitialState,
    });
    getFlashDraw();
  };

  const handleChange = (e: any) => {
    const name = e.currentTarget.name;
    const value = e.currentTarget.value;
    setTouch({
      ...touchState,
      [name]: true,
    });

    setErrors((state) => ({
      ...errors,
      [name]: { ...state[name], status: false },
    }));

    setState((state) => ({
      ...state,
      [name]: "" + value,
    }));
  };

  const trimValueOnBlur = (e) => {
    setState({
      ...state,
      [e.target.name]: state[e.target.name].trim(),
    });
  };

  const handleChangeBonusStatus = () => {
    setState({
      ...state,
      isActive: !state.isActive,
    });
  };

  const handleChangeDate = (e: any) => {
    const name = e.currentTarget.name;
    const value = e.currentTarget.value;

    setTouch({
      ...touchState,
      [name]: true,
    });

    setErrors(() => ({
      ...errors,
      endAt: { status: false, message: "" },
    }));

    setState((state) => ({
      ...state,
      [name]: "" + value,
    }));

    let endDate = 0;
    let startDate = 0;

    if (name === "endAt" && state.startAt) {
      endDate = value.getTime();
      startDate = new Date(state.startAt).getTime();
    } else if (name === "startAt" && state.endAt) {
      startDate = value.getTime();
      endDate = new Date(state.endAt).getTime();
    }

    if (endDate && startDate) {
      if (startDate >= endDate) {
        setErrors({
          ...errors,
          endAt: {
            status: true,
            message: "Start date cannot be later than finish date",
          },
        });
      }
    }
  };

  const handleSaveFlashDraw = async () => {
    let currentErrors = {};
    const skipRequiredCheck = ["_id", "__v", "isActive"];

    Object.keys(state).map((key) => {
      if (!state[key] || !state[key].length) {
        if (!skipRequiredCheck.includes(key)) {
          currentErrors[key] = { status: true, message: "Field is required" };
        }
      }
    });

    if (Object.keys(currentErrors).length !== 0 || errors.endAt.status) {
      setErrors({
        ...errors,
        ...currentErrors,
      });
      notify("Please check fields ", "error");
      return;
    } else {
      const data = {
        value: state.value,
        badgeURL: state.badgeURL,
        badgeMobileURL: state.badgeMobileURL,
        title: state.title,
        description: state.description,
        isActive: state.isActive,
        startAt: new Date(state.startAt).toUTCString(),
        endAt: new Date(state.endAt).toUTCString(),
      };

      try {
        const result: any = await dataProvider.editFlashDraw(
          "/flash-draw",
          data,
        );
        if (result.data) {
          notify("Flash draw edited", "info");
        }
      } catch (error: any) {
        notify(error.data.error, "error");
      }
    }
  };

  return (
    <div className="flash-draw">
      <div className="fieldsRow">
        <div className="side">
          <CreateBonusField
            fieldTitle="Bonus Value (e.g. £xx,xxx)"
            field={
              <Box width={"80%"}>
                <TextField
                  label=""
                  size="small"
                  variant="outlined"
                  type="text"
                  name="value"
                  error={errors.value.status}
                  onChange={handleChange}
                  value={state.value}
                  onBlur={trimValueOnBlur}
                />
              </Box>
            }
          />
          {errors.value.status && (
            <span className="fieldErrorMessage">{errors.value.message}</span>
          )}
        </div>
        <div className="side" style={{ flex: "0 0 120px" }}>
          <CreateBonusField
            fieldTitle="Active"
            fieldTemplate="inline"
            field={
              <Switch
                checked={state.isActive}
                onChange={handleChangeBonusStatus}
                color="primary"
                name="checkedB"
                inputProps={{ "aria-label": "primary checkbox" }}
              />
            }
          />
        </div>
      </div>
      <div className="fieldsRow">
        <div className="side">
          <CreateBonusField
            fieldTitle="BadgeURL"
            field={
              <Box width={"100%"}>
                <TextField
                  label=""
                  size="small"
                  variant="outlined"
                  type="text"
                  name="badgeURL"
                  error={errors.badgeURL.status}
                  onChange={handleChange}
                  value={state.badgeURL}
                  onBlur={trimValueOnBlur}
                />
              </Box>
            }
          />
          {errors.badgeURL.status && (
            <span className="fieldErrorMessage">{errors.badgeURL.message}</span>
          )}
        </div>
        <div className="side">
          <CreateBonusField
            fieldTitle="BadgeMobileURL"
            field={
              <Box width={"100%"}>
                <TextField
                  label=""
                  size="small"
                  variant="outlined"
                  type="text"
                  name="badgeMobileURL"
                  error={errors.badgeMobileURL.status}
                  onChange={handleChange}
                  value={state.badgeMobileURL}
                  onBlur={trimValueOnBlur}
                />
              </Box>
            }
          />
          {errors.badgeMobileURL.status && (
            <span className="fieldErrorMessage">{errors.badgeMobileURL.message}</span>
          )}
        </div>
      </div>
      <div className="fieldsRow">
        <div className="side">
          <CreateBonusField
            fieldTitle="Start Date"
            field={
              <DateTimePicker
                value={state.startAt && new Date(state.startAt)}
                format={"dd-MM-y"}
                name="startAt"
                onChange={(value: Date) =>
                  handleChangeDate({
                    currentTarget: { name: "startAt", value },
                  })
                }
                disableClock
                className={errors.startAt.status ? "error" : ""}
              />
            }
          />
          {errors.startAt.status && (
            <div className="dateErrorMessageWrapper">
              <span className="fieldErrorMessage">
                {errors.startAt.message}
              </span>
            </div>
          )}
        </div>
        <div className="side">
          <CreateBonusField
            fieldTitle="End Date"
            field={
              <DateTimePicker
                value={state.endAt && new Date(state.endAt)}
                format={"dd-MM-y"}
                name="endAt"
                onChange={(value: Date) =>
                  handleChangeDate({
                    currentTarget: { name: "endAt", value },
                  })
                }
                className={errors.endAt.status ? "error" : ""}
              />
            }
          />
          {errors.endAt.status && (
            <div className="dateErrorMessageWrapper">
              <span className="fieldErrorMessage">{errors.endAt.message}</span>
            </div>
          )}
        </div>
      </div>
      <div className="fieldsRow">
        <div className="side">
          <CreateBonusField
            fieldTitle="Title"
            field={
              <Box width={"100%"}>
                <TextField
                  label=""
                  size="small"
                  variant="outlined"
                  type="text"
                  name="title"
                  error={errors.title.status}
                  onChange={handleChange}
                  value={state.title}
                  onBlur={trimValueOnBlur}
                />
              </Box>
            }
          />
          {errors.title.status && (
            <span className="fieldErrorMessage">{errors.title.message}</span>
          )}
        </div>
      </div>
      <div className="fieldsRow">
        <div className="side">
          <CreateBonusField
            fieldTitle="Description"
            field={
              <Box width={"100%"}>
                <TextField
                  label=""
                  size="small"
                  variant="outlined"
                  type="text"
                  name="description"
                  error={errors.description.status}
                  onChange={handleChange}
                  value={state.description}
                  onBlur={trimValueOnBlur}
                />
              </Box>
            }
          />
          {errors.description.status && (
            <span className="fieldErrorMessage">
              {errors.description.message}
            </span>
          )}
        </div>
      </div>
      <div className="flash-draw__btns">
        <AppButton
          onClick={handleSaveFlashDraw}
          size="long"
          text="Save Flash Draw"
        />
        <AppButton onClick={handleFormCancel} size="long" text="Cancel" />
      </div>
    </div>
  );
};

export default FlashDraw;
